import {
  BackdropCircularProgress,
  EnhancedSnackbar,
  ITest,
  LabelPosition,
  RHookFormTextFieldWithLabel,
  SingleColumnLayout,
  StandardPanel,
  StandardSubmitFormButtonsToolbar,
} from "@websential/cosmic";
import { FC, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { INITIAL_SNACKBAR_DATA, QUERY_OPTIONS } from "../../../common/constants";
import { IResponse, IServerError } from "../../../models/common";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";

import { FlexFieldPanelService } from "../../../services/FlexFieldPanelService";
import { Grid } from "@mui/material";
import { IFlexFieldPanelRequestPayload } from "../../../models/flexFieldPanel/IFlexFieldPanelRequestPayload";
import { IFlexFieldPanelResponsePayload } from "../../../models/flexFieldPanel";
import { instanceOfIFlexFieldPanelLabelSetupRequest } from "../../../common/instance-method";
import { validationSchema } from "../validator";
import { yupResolver } from "@hookform/resolvers/yup";

const DEFAULT_VALUES = {
  label: "",
  appLabel: "",
};

const FLEX_FIELD_PANEL_DATA_QUERY_KEY = "get-flex-field-tab-data";

const useGetData = (id: string | undefined) =>
  useQuery({
    queryKey: [FLEX_FIELD_PANEL_DATA_QUERY_KEY],
    queryFn: () => { return FlexFieldPanelService.getById(id) },
    refetchOnWindowFocus: false
  });

export interface IFlexFieldPanelSetup extends ITest { }

export const FlexFieldPanelSetup: FC<IFlexFieldPanelSetup> = ({ testId }) => {
  const navigate = useNavigate();
  const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
  const queryClient = useQueryClient();
  const { id } = useParams();



  const { isPending: isSubmitting, mutate: submitFormFields } = useMutation({
    mutationFn: FlexFieldPanelService.create,

    onSuccess: (
      res: IFlexFieldPanelResponsePayload | IServerError | IResponse | any
    ) => {
      if (instanceOfIFlexFieldPanelLabelSetupRequest(res)) {

        queryClient.invalidateQueries({
          queryKey: [FLEX_FIELD_PANEL_DATA_QUERY_KEY,
            res.id,],
        });
        setSnackbarData({
          open: true,
          message: "Data has been submitted successfully!",
          severity: "success",
        });
      }
      let path = "/flex-field-panels/list";
      navigate(path);
    },
    onError: (err) => {
      // TODO: customize the toast message based on the response
      setSnackbarData({
        open: true,
        message: "Something went wrong, try again later!",
        severity: "error",
      });
    },
  }
  );

  const formMethods = useForm<IFlexFieldPanelRequestPayload>({
    resolver: yupResolver(validationSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: DEFAULT_VALUES,
  });

  const { isPending: isLoadingData, data: selectedFlexFieldPanelLabelData } =
    useGetData(id);

  const onSubmit = (formData: IFlexFieldPanelRequestPayload) => {
    submitFormFields(formData);
  };

  const onResetClicked = () => {
    setSnackbarData({
      open: true,
      message: "Form was reset successfully",
      severity: "success",
    });
    formMethods.reset(DEFAULT_VALUES);
  };

  const onSnackbarClosed = (
    _event: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarData(INITIAL_SNACKBAR_DATA);
  };

  useEffect(() => {
    // if the selected flex field data has loaded.
    if (selectedFlexFieldPanelLabelData) {
      Object.keys(selectedFlexFieldPanelLabelData).forEach((fieldName) => {

        // @ts-ignore
        const fieldValue = selectedFlexFieldPanelLabelData[fieldName];

        // @ts-ignore
        formMethods.setValue(fieldName, fieldValue ?? undefined);
      });
    }
  }, [selectedFlexFieldPanelLabelData, formMethods.setValue, formMethods]);

  return (
    <Grid
      size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
      sx={{ "& .MuiAppBar-root": { zIndex: 2, top: "65px" } }}
    >
      <BackdropCircularProgress
        open={isSubmitting || isLoadingData}
        testId={testId}
      />
      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit(onSubmit)}>
          <SingleColumnLayout
            toolbar={
              <StandardSubmitFormButtonsToolbar
                onResetClicked={onResetClicked}
                isSubmitting={false}
                testId={""}
              />
            }
            testId={"flex-field-tab-setup-toolbar"}
          >
            <StandardPanel
              title="Flex Field Panel"
              leftSpacing={2}
              rightSpacing={2}
              testId={`${testId}-flex-field-panel`}
            >
              <RHookFormTextFieldWithLabel
                label="Label"
                id="label"
                testId={`${testId}-label`}
                placeholder="Panel Label"
                labelPosition={LabelPosition.Left}
                required
              />
              <RHookFormTextFieldWithLabel
                label="App Label"
                id="appLabel"
                testId={`${testId}-app-label`}
                placeholder="App Label"
                labelPosition={LabelPosition.Left}
                required
              />
            </StandardPanel>
          </SingleColumnLayout>
        </form>
      </FormProvider>
      <EnhancedSnackbar
        message={snackbarData.message}
        onCloseHandler={onSnackbarClosed}
        severity={snackbarData.severity}
        testId={`${testId}-snackbar`}
        open={snackbarData.open}
      />
    </Grid>
  );
};
